<template>
  <div class="pt-4 pb-0 px-4">
    <h1 class="dialog-title mb-2">Confira o que encontramos</h1>

    <div v-if="showAdjustCorrectLines" class="d-flex align-center positive-item">
      <mf-icon icon="check" size="24" color="verde-l10" />
      <p class="mb-0">{{ adjustCorrectLinesMessage }}</p>
    </div>

    <div v-if="showAdjustErrorLines" class="d-flex align-center negative-item">
      <mf-icon icon="close" size="24" color="vermelho-0" />
      <p class="mb-0">{{ adjustErrorLinesMessage }}</p>
      <span class="export-link" @click="downloadCsv">Clique aqui para exportar o arquivo</span>
    </div>
    <p class="dialog-subtitle my-6">{{ dialogSubtitle }}</p>
    <mf-info-card variant="info" :v-html="infoCardHtml" class="mb-3 mt-0" />
    <include-emails-table
      v-if="showAdjustCorrectLines"
      ref="includeEmailsTable"
      class="pa-0 mb-5"
      use-only-logged-user-email
      :emails.sync="emails"
      scroll-height="200px"
    />
    <mf-action-buttons
      class="pb-3"
      :primary-button="primaryButton"
      :secondary-button="{
        text: 'Cancelar',
        action: () => $emit('cancel'),
        isVisible: showAdjustCorrectLines,
        isLoading: loading,
      }"
    />
  </div>
</template>

<script>
import { createCsvFromArray } from '@/config/helpers.js'
import IncludeEmailsTable from '@/components/export-email-report/IncludeEmailsTable.vue'

export default {
  name: 'ApproveCashbackDialogContent',
  components: { IncludeEmailsTable },
  props: {
    adjustCashbackData: {
      type: Object,
      default: () => {},
    },
    loading: {
      type: Boolean,
      default: true,
    },
  },
  data: () => ({
    emails: [],
  }),
  computed: {
    infoCardHtml() {
      return '<span style="color: #004D99">Para concluir a importação, é necessário clicar no botão "Aprovar"</span>'
    },
    primaryButton() {
      return {
        text: this.showAdjustCorrectLines ? 'Aprovar' : 'Voltar',
        action: this.showAdjustCorrectLines ? this.approveAdjust : () => this.$emit('cancel'),
        isVisible: true,
        isLoading: this.loading,
      }
    },
    showAdjustCorrectLines() {
      return this.adjustCashbackData?.success?.adjustments_to_be_made > 0
    },
    adjustCorrectLinesMessage() {
      const count = this.adjustCashbackData?.success?.adjustments_to_be_made
      return count === 1 ? '1 linha corretas para importação.' : `${count} linhas corretas para importação.`
    },
    showAdjustErrorLines() {
      return this.adjustCashbackData?.failure?.total_failures > 0
    },
    adjustErrorLinesMessage() {
      const count = this.adjustCashbackData?.failure?.total_failures
      return count === 1 ? '1 linha com algum erro.' : `${count} linhas com algum erro.`
    },
    dialogSubtitle() {
      return this.showAdjustCorrectLines
        ? 'O fluxo de ajuste de cashback foi alterado e agora é realizado de forma assíncrona. Um e-mail será enviado para os endereços de e-mail listados abaixo com o resultado de todos os ajustes.'
        : 'Todas as linhas do arquivo possuem algum erro, portanto não será possível aprovar essa importação. Exporte o arquivo para visualizar os erros e tente novamente com o arquivo corrigido.'
    },
    errors() {
      return {
        BULK_ADJUST_CASHBACK_INCORRECT_COLUMN: 'Coluna incorreta',
        BULK_ADJUST_CASHBACK_INCORRECT_VALUE: 'Valor do ajuste incorreto',
        BULK_ADJUST_CASHBACK_INCORRECT_REASON: 'Motivo do ajuste incorreto',
        BULK_ADJUST_CASHBACK_INVALID_EXPIRATION_DATE_FORMAT: 'Formato da data de expiração inválido',
        BULK_ADJUST_CASHBACK_EXPIRATION_DATE_NOT_IN_FUTURE: 'A Data de expiração não pode ser menor ou igual ao dia de hoje',
        BULK_ADJUST_CASHBACK_DEBIT_WITH_EXPIRATION_DATE: 'O ajuste de débito não pode ter data de expiração',
        BULK_ADJUST_CASHBACK_DEBIT_WITH_AWARDS_REASON: 'O ajuste de débito não pode ser uma premiação',
        BULK_ADJUST_CASHBACK_INSUFFICIENT_UNUSED_CASHBACK: 'Saldo de cashback do usuário insuficiente',
        BULK_ADJUST_CASHBACK_UNFOUND_USER: 'Usuário não encontrado',
        BULK_ADJUST_CASHBACK_INVALID_EXPIRATION_DATE_VALUE: 'Data inválida',
      }
    },
  },
  methods: {
    approveAdjust() {
      const isValid = this.$refs.includeEmailsTable.isValid()
      if (!isValid) return
      this.$emit('approve', this.emails)
    },
    async downloadCsv() {
      const csvHeaders = ['CPF', 'Valor', 'Motivo', 'Expiração', 'Erro']
      const dataCSV = this.adjustCashbackData.failure.failed_lines_examples.map((item) => {
        return {
          cpf: item.document_no.toString(),
          value: item.adjustment_amount,
          reason: item.adjustment_reason,
          expires_at: item?.expires_at || '',
          error: this.errors[item.reason],
        }
      })
      createCsvFromArray(dataCSV, 'ajustes_com_erros', csvHeaders)
    },
  },
}
</script>

<style lang="scss" scoped>
.dialog-title {
  font-weight: 700;
  color: $cinza-d10;
  font-size: 22px;
  line-height: 120%;
}
.dialog-subtitle {
  font-weight: 400;
  color: $cinza-0;
  font-size: 14px;
}

.positive-item,
.negative-item {
  font-size: 14px;
  font-weight: 400;
  line-height: 16.8px;

  p {
    margin: 0 4px;
  }
}

.positive-item {
  p {
    color: $verde-0;
  }
}

.negative-item {
  p {
    color: $vermelho-d20;
  }
  .export-link {
    color: $vermelho-0;
    font-weight: 600;
    text-decoration: underline;
    cursor: pointer;
  }
}
</style>
